var _ = require("lodash");
var { FunctionId } = require("../../../typing/typing");

/**
 * Collection of lambda term reduction.
 *
 * General information and argeements:
 * A reduction is a function of two mandatory arguments `term` and `reduce`.
 * The first argument `term` is a lambda term to reduce.
 * The second argument is a reduction (the same or some other one)
 * A reduction returns `undefined` if `term` is not reducible
 *   and a lambda term otherwise.
 * A reduction might not be explicitly closed under a structure of lambda term.
 * However, `reduce` may be called to reduce subterms, according to
 *   implemented logic.
 *
 * @module module:Lambda/Reductions
 */

var
  Application = require("./Constructors").Application,
  Var         = require("./Constructors").Var,
  Lambda      = require("./Constructors").Lambda,
  External    = require("./Constructors").External;
var
  replaceVar = require("./Substitutions").replaceVar;
var
  ExternalObjectInterpretation =
    require("./Externals").ExternalObjectInterpretation;
  

/**
 * @summary One-step beta reduction.
 *
 * @description
 * Designed to be closed under
 * ```
 *       M  →  N
 *       ———————
 *       MP → NP
 * ```
 * @param {LambdaTerm} term - a lambda term to reduce
 * @param {LambdaReduce} reduce - a function
 *   that will be called to reduce `M` in the term `(MN)`.
 *   Basically, it is a reduction composition
 *   (see {@link module:Lambda/Reduction~ComposeReductions})
 *   that includes `BetaReduction`.
 *   If `reduce` undefined, the applying subterm `M` is not reduced.
 * @return `undefined` or LambdaTerm.
 *   - `undefined` if a term is not an application
 *   - `(M' N)` if `term=(MN)` and `M` is reduced to `M'`
 *     with given `reduce` function
 *   - `M[x/N]` if `term=((\x. M)N)`
 * @see module:Lambda#replaceVar
 **/
function BetaReduction(term, reduce) {
  if(term instanceof Var && term.value !== undefined) {
    return term.value; // TODO: FIX!
  }
  if(term instanceof Application) {
    if(term.func instanceof Lambda) {
      term.func.arg.value = term.arg; // TODO: FIX!
      return replaceVar(term.func.body, term.func.arg, term.arg);
    }
  }
}

/**
 * 
 */
function AppReduction(term, reduce) {
  if(term instanceof Application) {
    if(reduce !== undefined) {
      var func1 = reduce(term.func, reduce);
      var arg1  = reduce(term.arg, reduce);
      if(func1 !== undefined || arg1 !== undefined)
        return new Application(func1 || term.func, arg1 || term.arg);
    }
  }
}

/**
 * REDUCTION OF LAMBDA TERMS IN EXTERNAL STRUCTURE of given type.
 * @param {String} objType - external object type
 * @return reduction of External lambda terms with given objType
 */
function ExternalReduction(objType) {
  /**
   * @param {LambdaTerm} term - a term to reduce
   * @param {LambdaReduce} reduce - a function called to reduce subterms
   * @return `undefined` or LambdaTerm
   *   - `undefined` if a term is not an external object
   *   - `undefined` if an external object does not contain any reducible subterm
   *   - an external object of the same type
   *     with all the reducible subterms reduced
   * @see module:Lambda.Externals.ExternalObjectInterpretation
   */
  function SpecificExternalReduction(term, reduce) {
    /* a reduction to be called on subterms.
      It set flag to true if at least one subterm is reduced
      and returns one-step reduced subterm or the unconverted subterm */
    function subreduction(t) {
      var reducedTerm = reduce(t, reduce);
      if(reducedTerm === undefined) return t;
      flag = true;
      return reducedTerm;
    }
    FunctionId(reduce);
    var flag = false;
    if(!ExternalObjectInterpretation[objType] ||
      !ExternalObjectInterpretation[objType].map)
        return undefined;
    if(term instanceof External && term.objType === objType) {
      // if External object does not provide `map` function,
      // there is nothing to do
      // otherwise use its map function
      // Note, that getExternalMap can be used here,
      // but it seems to be more convenient to extract `map` immediately
      var map = ExternalObjectInterpretation[term.objType].map;
      var r = map(term.object, subreduction);
      return flag ? new External(r, term.objType, term.termType) : undefined;
    }
  }
  return SpecificExternalReduction;
}

/**
 * @summary MANY-STEP REDUCTION.
 * @description
 * Let `r` be a reduction. `ManyStepReduction(r)`
 *   {@link module:Lambda.Reduction~msreduction returns a reduction} that
 *   applies `r` to a term iteratively until its normal form is reached.
 * A reduction may be specified explicitely, by a list of reductions
 *   or by default list of reductions, see below.
 * Note that `ManyStepReduction(r)` returns a reduction, which second argument 
 *   passes to the second argument of the reduction.
 * @param {LambdaReduction} reduction - one-step reduction
 * @return LambdaReduction
 * @see module:Lambda.Reduction.ComposeReductions
 * @see module:Lambda.Reduction~msreduction
 */
function ManyStepReduction(reduction) {
  var n = 0;
  /**
   * Many-step closure of given reduction.
   * @param {LambdaTerm} term - a term to reduce
   * @param {LambdaReduce} reduce - a function called to reduce subterms
   * @return `undefined` or LambdaTerm
   *   - undefined if the term is in normal form
   *   - a normal form of the term
   * @see module:Lambda.Reduction.ManyStepReduction
   */
  function msreduction(term, reduce) {
    var red = term, reducible = false;
    while(red !== undefined) {
      term = red;
      red  = reduction(term, reduce);
      reducible = reducible || red !== undefined;
      ++n;
      if(n > 10000) throw "to many reductions!";
    }
    return reducible ? term : undefined;
  }
  return msreduction;
}

/**
 * A composition of reductions
 * It is one-step reduction.
 * If two or more reductions can be applied, the one with the least index
 *   is applied. Therefore, an order might affect.
 * The second argument of returned reduction distributes to each reduction
 *   of the list
 * @param list - an array of reductions
 * @return a reduction that sequentially applies each reduction of the list
 *   until non-undefined returned or the reached.
 */
function ComposeReductions(list) {
  if(!list instanceof Array) throw "ComposeReductions expects a list";
  return function reduction(term, reduce) {
    var reducible = false;
    for(var i = 0; i < list.length; ++i) {
      var r = list[i](term, reduce);
      if(r !== undefined) { term = r; reducible = true }
    }
    return reducible ? term : undefined;
  };
}

/**
 * Closure under the structure
 * Note that returned reduction ignores its second argument.
 * @param reduce - a reduction
 * @return a reduction that calles `reduce` passing itself
 *   to the second argument
 */
function ClosedReduction(reduce) {
  return function reduction(term) {
    return reduce(term, reduction);
  };
}

/**
 * Default reduction (many-step reduction closed over the structure).
 * @param term - a term to reduce
 * @param reduction - a specification of reduction to use:
 *  - `undefined` - use {@link Lambda.Reduction.DefaultReductions}
 *  - a reduction - use that reduction
 *  - a list of reductions - use a composition of them
 * @return reduced term or undefined
 */
function reduce(term, reduction) {
  if(reduction === undefined)
    reduction = ComposeReductions(_export.DefaultReductions);
  if(_.isArray(reduction))
    reduction = ComposeReductions(reduction);
  return ManyStepReduction(ClosedReduction(reduction))(term);
}

var _export = {
  /**
   * @summary array of default reductions,
   * i.e. reductions functions like `reduce` use by default
   * if `reduce` argument left undefined.
   * @memberof module:Lambda/Reductions
   * @instance
   */
  DefaultReductions: [
    AppReduction,
    BetaReduction
  ],
  BetaReduction: BetaReduction,
  ExternalReduction: ExternalReduction,
  ClosedReduction: ClosedReduction,
  ComposeReductions: ComposeReductions,
  ManyStepReduction: ManyStepReduction,
  reduce: reduce
};

module.exports = _export;
